/* NameCollab v18 — reliable milestone gift unboxing.
   Replays on initial product display, quantity change, Quick View and section rerender.
   Visual animation only: does not change prices, variants, add-to-cart or cart warranty. */
(() => {
  'use strict';
  if (window.__namecollabGiftReveal18) return;
  window.__namecollabGiftReveal18 = true;

  const ANIMATION_MS = 1250;
  const activeTimers = new WeakMap();
  const motionDisabled = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function quantityFor(root) {
    const scope = root.closest('product-info') || root.closest('.shopify-section') || root.parentElement;
    const breaks = scope?.querySelector('quantity-breaks');
    if (breaks) {
      const active = breaks.querySelector('input.quantity-breaks__radio:checked');
      if (!active) return 0;
      const label = active.nextElementSibling?.classList.contains('quantity-break')
        ? active.nextElementSibling
        : breaks.querySelector(`label[for="${CSS.escape(active.id)}"]`);
      return Math.max(0, Number(label?.dataset.quantity || active.value || 0) || 0);
    }
    return Math.max(0, Number(scope?.querySelector('input[name="quantity"]')?.value || 1) || 0);
  }

  function stop(item) {
    const timer = activeTimers.get(item);
    if (timer) clearTimeout(timer);
    activeTimers.delete(item);
    item.classList.remove('is-unboxing');
  }

  function play(item) {
    if (motionDisabled()) return;
    stop(item);
    // Needed to replay a CSS keyframe even after a customer reselects a pack.
    void item.offsetWidth;
    item.classList.add('is-unboxing');
    activeTimers.set(item, setTimeout(() => {
      item.classList.remove('is-unboxing');
      activeTimers.delete(item);
    }, ANIMATION_MS));
  }

  function update(root) {
    if (!root.isConnected) return;
    const count = quantityFor(root);
    const exact = root.dataset.unlockMode === 'exact';
    root.querySelectorAll('[data-gift-item]').forEach(item => {
      const threshold = Number(item.dataset.minQuantity || 1);
      const shouldUnlock = count > 0 && (exact ? threshold === count : threshold <= count);
      const initialized = item.dataset.giftInitialized === 'true';
      const wasUnlocked = item.dataset.unlocked === 'true';
      const unlocked = item.querySelector('[data-gift-unlocked]');
      const locked = item.querySelector('[data-gift-locked]');
      if (!unlocked || !locked) return;

      item.dataset.giftInitialized = 'true';
      item.dataset.unlocked = String(shouldUnlock);
      unlocked.hidden = !shouldUnlock;
      locked.hidden = shouldUnlock;

      if (!shouldUnlock) {
        if (wasUnlocked || item.classList.contains('is-unboxing')) stop(item);
      } else if (!initialized || !wasUnlocked) {
        // v17 skipped this on first render. v18 deliberately animates new DOM
        // because Shopify sometimes replaces the block after bundle selection.
        play(item);
      }
      // Keep an in-flight animation alive on unrelated DOM and input updates.
    });
  }

  function updateAll() {
    document.querySelectorAll('[data-tst-quantity-gifts]').forEach(update);
  }

  let frame = 0;
  let followup = 0;
  function schedule() {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      updateAll();
    });
    // Some theme scripts update the checked property after their click handler.
    clearTimeout(followup);
    followup = setTimeout(updateAll, 110);
  }

  const affectsQuantity = element => element?.matches?.('input.quantity-breaks__radio,input[name="quantity"]');
  document.addEventListener('change', event => { if (affectsQuantity(event.target)) schedule(); }, true);
  document.addEventListener('input', event => { if (affectsQuantity(event.target)) schedule(); }, true);
  document.addEventListener('click', event => {
    if (event.target.closest?.('quantity-breaks .quantity-break, quantity-breaks input.quantity-breaks__radio')) schedule();
  }, true);
  document.addEventListener('shopify:section:load', schedule);

  // Quick View can replace the entire product-info fragment. Observe new
  // blocks, not just page load. Existing blocks retain their active animation.
  const observer = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-tst-quantity-gifts],quantity-breaks') ||
            node.querySelector?.('[data-tst-quantity-gifts],quantity-breaks')) {
          schedule();
          return;
        }
      }
    }
  });

  function init() {
    updateAll();
    observer.observe(document.body, {childList:true,subtree:true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
})();
